import React from "react";
import { Col } from "react-bootstrap";
import { IControlStyles, PageContent, useControlStyles } from "../../assets/layouts";
import { VfxLib } from "../../assets/vfx";
import Control from "../../assets/vfx/controls/control";
import { SearchDto, SearchOperator, SortModel, SortDirection } from "../../assets/utils/lib/searchDto";
import { GRID_ACTION_TYPES, sortBy as defaultSort, defaultControlledSearchQuery } from "../../assets/types/common";
import { Constants } from "../../assets/utils/lib/constants";
import Form from "../../assets/vfx/form/form";
import { SearchContainer } from "../../assets/vfx/search/searchContainer";
import { CommunityService } from "./communityService";
import { ViewProps } from "./props";
import { ClaimHelper, FormatHelper } from "../../assets/utils/helpers";
import { roles } from "../../assets/configuration/claimSettings";

interface CommunityPageProps {
    controlStyles?: IControlStyles | undefined;
    onCustomEvent?: (actionType: string, dataId: string) => void;
    lookups: ViewProps.CommunityLookupProps | undefined;
    query?: SearchDto;
    onQueryChange?: (query: SearchDto) => void;
}

const sortBy: SortModel = defaultSort ?? { field: "name", direction: SortDirection.Ascending };

type GridPatch = Partial<{ pageNumber: number; pageSize: number; sortBy: SortModel }>;

export const CommunityPage = (props: CommunityPageProps) => {
    const [searchQuery, setSearchQuery] = React.useState<SearchDto>(
        props.query ?? {
            ...defaultControlledSearchQuery,
            sortBy: defaultControlledSearchQuery.sortBy ?? sortBy,
            sorts: defaultControlledSearchQuery.sorts ?? [defaultControlledSearchQuery.sortBy ?? sortBy],
        }
    );

    // Keep a ref so onGridPatch can read current state synchronously without stale closures
    const searchQueryRef = React.useRef(searchQuery);
    searchQueryRef.current = searchQuery;

    // Helper: update local state AND bubble up to container so navigation restores position
    const applyQuery = React.useCallback((next: SearchDto) => {
        setSearchQuery(next);
        props.onQueryChange?.(next);
    }, [props.onQueryChange]);

    const isSystemAdmin = React.useMemo(
        () => ClaimHelper.hasRole([roles.SystemAdmin]),
        []
    );

    const communityGridQuery = CommunityService.gridQuery(searchQuery, isSystemAdmin);
    const isGridLoading = communityGridQuery.isLoading || communityGridQuery.isFetching;
    const styles: IControlStyles = props.controlStyles ?? useControlStyles();

    const handleSearch = (values: { name?: string }) => {
        applyQuery({
            filters: [
                { field: "name", operator: SearchOperator.StartsWith, value: values.name ?? Constants.emptyString },
                { field: "isDeleted", value: false },
            ],
            pageSize: 10,
            pageNumber: 1,
            sortBy,
            sorts: [sortBy],
        });
    };

    const handleNew = () =>
        props.onCustomEvent?.(GRID_ACTION_TYPES.NEW, Constants.emptyGuid);

    const handleClear = () => {
        applyQuery({
            ...defaultControlledSearchQuery,
            pageSize: searchQueryRef.current.pageSize ?? 10,
            sorts: [searchQueryRef.current.sortBy ?? sortBy],
        });
    };

    const onGridPatch = React.useCallback((patch: GridPatch) => {
        const prev = searchQueryRef.current;
        const next: SearchDto = {
            ...prev,
            pageNumber: patch.pageNumber ?? prev.pageNumber,
            pageSize: patch.pageSize ?? prev.pageSize,
            ...(patch.sortBy ? { sortBy: patch.sortBy, sorts: [patch.sortBy] } : null),
        };

        // No-op guard: avoid a second fetch if nothing effectively changed
        const samePage = next.pageNumber === prev.pageNumber && next.pageSize === prev.pageSize;
        const a = prev.sorts?.[0]; const b = next.sorts?.[0];
        const sameSort = (!!a || !!b) && a?.field === b?.field && a?.direction === b?.direction;

        if (!samePage || !sameSort) {
            setSearchQuery(next);
            props.onQueryChange?.(next); // keep container in sync so nav-back restores position
        }
    }, [props.onQueryChange]);

    const searchContainer: React.JSX.Element = (
        <SearchContainer
            initialValues={{ name: Constants.emptyString }}
            onSearch={handleSearch}
            onClear={handleClear}
            enableReinitialize
        >
            <Col lg={4}>
                <Form.Text name="name" label="Name" groupType="topLabel" />
            </Col>
        </SearchContainer>
    );

    return (
        <PageContent hideHeader>
                <Control.DataGrid
                    dataId="id"
                    height="55vh"
                    cols={[
                        { data: "id", title: "ID", visible: false, columnType: "number", alignment: "right" },
                        { data: "name", title: "Name", visible: true, columnType: "text", alignment: "left", sortable: true, width: "18%" },
                        { data: "city", title: "City", visible: true, columnType: "text", alignment: "left", sortable: true, width: "18%" },
                        /*{ data: "state", title: "State", visible: true, columnType: "text", alignment: "left", sortable: true, width: "18%" },*/
                        {
                            data: "activationDate", title: "Active Date", visible: true, columnType: "custom", alignment: "left", sortable: true, width: "18%", customCell: (activationDate: Date | null | string) => {
                                const date =
                                    activationDate instanceof Date
                                        ? activationDate
                                        : activationDate
                                            ? new Date(activationDate)
                                            : null;
                                return date && !isNaN(date.getTime()) ? FormatHelper.formatDate(date) : "";
                            },
                        },
                        { data: "description", title: "Description", visible: true, columnType: "text", alignment: "left", width: "24%" },
                    ]}
                    actionCol={{
                        data: "id",
                        title: "Action",
                        visible: true,
                        columnType: "action",
                        alignment: "right",
                        width: "100px",
                        controlStyles: styles,
                        actionButtons: [VfxLib.datagridActionButtons({ datagridStyles: styles }).view],
                    }}
                    onCustomEvent={props.onCustomEvent}
                    data={communityGridQuery?.data?.result ?? []}
                    isActionButtonClick
                    totalRecords={communityGridQuery?.data?.totalSize ?? (communityGridQuery?.data?.result?.length ?? 0)}
                    query={searchQuery}             // same object for grid + API
                    loading={isGridLoading}
                    onQueryChange={onGridPatch}     // tiny guard prevents duplicate refetch
                    uiOptions={{
                        title: "Community Lookup",
                        filterContent: searchContainer,
                        primaryAction: isSystemAdmin
                            ? {
                                text: "New",
                                icon: "bi bi-plus-lg",
                                onClick: handleNew,
                            }
                            : undefined,
                    }}
                />
        </PageContent>
    );
};
